import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AlertTriangle, TrendingUp, Globe, Copy, HelpCircle, Zap, Fuel, Percent } from "lucide-react";

const KIND_META = {
    high_amount: { icon: TrendingUp, label: "High value", cls: "border-red-200 bg-red-50 text-red-700" },
    international: { icon: Globe, label: "International", cls: "border-blue-200 bg-blue-50 text-blue-700" },
    duplicate: { icon: Copy, label: "Duplicate", cls: "border-amber-200 bg-amber-50 text-amber-700" },
    unknown_merchant: { icon: HelpCircle, label: "Unknown merchant", cls: "border-stone-200 bg-stone-50 text-stone-700" },
    burst_same_day: { icon: Zap, label: "Multiple same day", cls: "border-amber-200 bg-amber-50 text-amber-700" },
    cash_withdrawal: { icon: TrendingUp, label: "Cash withdrawal", cls: "border-red-200 bg-red-50 text-red-700" },
    late_fee: { icon: AlertTriangle, label: "Late fee", cls: "border-red-200 bg-red-50 text-red-700" },
    interest_charged: { icon: Percent, label: "Interest", cls: "border-red-200 bg-red-50 text-red-700" },
    forex_markup: { icon: Globe, label: "Forex markup", cls: "border-blue-200 bg-blue-50 text-blue-700" },
    fuel_surcharge: { icon: Fuel, label: "Fuel surcharge", cls: "border-amber-200 bg-amber-50 text-amber-700" },
};

const STATUSES = ["Review Pending", "Checked and Approved", "Dispute Required", "Closed"];

export default function Alerts() {
    const { hasRole } = useAuth();
    const [rows, setRows] = useState([]);
    const [cards, setCards] = useState([]);
    const [kind, setKind] = useState("all");
    const [status, setStatus] = useState("all");

    const load = () => {
        const params = {};
        if (kind !== "all") params.kind = kind;
        if (status !== "all") params.status = status;
        api.get("/alerts", { params }).then((r) => setRows(r.data));
    };
    useEffect(() => {
        api.get("/cards").then((r) => setCards(r.data));
    }, []);
    useEffect(() => { load(); }, [kind, status]);

    const setAlertStatus = async (id, newStatus) => {
        try {
            await api.patch(`/alerts/${id}`, { status: newStatus });
            toast.success("Updated");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const cardMap = Object.fromEntries(cards.map((c) => [c.id, c]));

    return (
        <div data-testid="alerts-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Unusual Transactions</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Anomalies to review.</h1>
                    <p className="text-sm text-stone-500 mt-3">{rows.length} alerts · {rows.filter(r => r.status === "Review Pending").length} pending review</p>
                </div>
                <div className="flex gap-2">
                    <Select value={kind} onValueChange={setKind}>
                        <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All kinds</SelectItem>
                            {Object.keys(KIND_META).map((k) => <SelectItem key={k} value={k}>{KIND_META[k].label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="space-y-3">
                {rows.map((a) => {
                    const meta = KIND_META[a.kind] || { icon: AlertTriangle, label: a.kind, cls: "border-stone-200 bg-stone-50 text-stone-700" };
                    const Icon = meta.icon;
                    const c = cardMap[a.card_id];
                    return (
                        <div key={a.id} className={`border rounded-lg p-4 flex items-start gap-4 ${a.status === "Closed" ? "opacity-60" : ""}`}>
                            <div className={`p-2 rounded-md border ${meta.cls}`}>
                                <Icon size={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-baseline gap-2">
                                    <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
                                    <span className="text-sm font-medium text-stone-900">{a.label}</span>
                                </div>
                                <div className="text-xs text-stone-500 mt-1.5">
                                    {c ? `${c.bank_name} · •••• ${c.last_4}` : ""} · {fmtDate(a.date)} · {fmtMoney(a.amount)}
                                </div>
                                {a.reviewed_by && <div className="text-[11px] text-stone-400 mt-1">Reviewed by {a.reviewed_by} · {fmtDate(a.reviewed_at)}</div>}
                            </div>
                            <div>
                                {hasRole("admin", "finance") ? (
                                    <Select value={a.status} onValueChange={(v) => setAlertStatus(a.id, v)}>
                                        <SelectTrigger className="h-8 w-[180px] text-xs" data-testid={`alert-status-${a.id}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                    </Select>
                                ) : (
                                    <span className="text-xs text-stone-500">{a.status}</span>
                                )}
                            </div>
                        </div>
                    );
                })}
                {rows.length === 0 && (
                    <div className="border border-dashed border-stone-300 rounded-lg p-14 text-center text-stone-500">
                        No alerts to review. Nice — everything looks normal.
                    </div>
                )}
            </div>
        </div>
    );
}
